import { useEffect, useState } from "react";
import { AlertTriangle, Lock, X } from "lucide-react";

import { Modal } from "@/components/Modal";
import { useApp, type LiveListing } from "@/context/AppContext";
import {
  getEbayListingState,
  reviseEbayListingDetails,
} from "@/features/ebayListings";

interface Props {
  listing: LiveListing | null;
  onClose: () => void;
  onSuccess: (text: string) => void;
}

export function EditDetailsModal({ listing, onClose, onSuccess }: Props) {
  const { products, updateLiveListing, updateProduct } = useApp();
  const open = listing !== null;
  const product = listing
    ? products.find((p) => p.id === listing.productId)
    : undefined;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [originalTitle, setOriginalTitle] = useState("");
  const [originalDescription, setOriginalDescription] = useState("");
  const [titleLockReason, setTitleLockReason] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!listing) {
      setTitle("");
      setDescription("");
      setOriginalTitle("");
      setOriginalDescription("");
      setTitleLockReason(null);
      setLoadError(null);
      setError(null);
      setLoading(false);
      return;
    }
    const initialTitle = listing.title ?? "";
    const initialDescription = product?.description ?? "";
    setTitle(initialTitle);
    setDescription(initialDescription);
    setOriginalTitle(initialTitle);
    setOriginalDescription(initialDescription);
    setError(null);
    setTitleLockReason(null);
    setLoadError(null);

    if (!listing.ebayListingId) {
      setLoadError("This listing has no eBay item ID — cannot revise details.");
      return;
    }

    let cancelled = false;
    setLoading(true);
    void getEbayListingState(listing.ebayListingId)
      .then((s) => {
        if (cancelled) return;
        const hasBids = s.bidCount > 0;
        const endingSoon = s.endTime
          ? new Date(s.endTime).getTime() - Date.now() < 12 * 60 * 60 * 1000
          : false;
        if (s.listingType === "Auction" && (hasBids || endingSoon)) {
          setTitleLockReason(
            hasBids
              ? `Title is locked — auction already has ${s.bidCount} bid${s.bidCount === 1 ? "" : "s"}. Description can still be edited.`
              : "Title is locked — auction ends within 12 hours. Description can still be edited.",
          );
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(
          err instanceof Error ? err.message : "Could not load listing details",
        );
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [listing, product?.description]);

  if (!listing) return null;

  const titleLocked = titleLockReason !== null;
  const titleTrimmed = title.trim();
  const descTrimmed = description.trim();
  const titleChanged = !titleLocked && titleTrimmed !== originalTitle.trim();
  const descChanged = descTrimmed !== originalDescription.trim();

  const submit = async () => {
    if (!listing.ebayListingId) return;
    setError(null);

    if (!titleChanged && !descChanged)
      return setError("Change the title or description before saving");
    if (titleChanged && titleTrimmed.length === 0)
      return setError("Title can't be empty");
    if (titleChanged && titleTrimmed.length > 80)
      return setError("Title must be 80 characters or fewer");
    if (descChanged && descTrimmed.length === 0)
      return setError("Description can't be empty");

    const body: { itemId: string; title?: string; description?: string } = {
      itemId: listing.ebayListingId,
    };
    if (titleChanged) body.title = titleTrimmed;
    if (descChanged) body.description = descTrimmed;

    setSubmitting(true);
    try {
      await reviseEbayListingDetails(body);
      if (titleChanged) {
        updateLiveListing({ ...listing, title: titleTrimmed });
      }
      // Persist back to source product so reopening the editor (or relisting
      // later) pre-fills the latest values rather than the stale cached copy.
      if (product) {
        const productPatch: Partial<typeof product> = {};
        if (titleChanged && product.listingTitle !== titleTrimmed) {
          productPatch.listingTitle = titleTrimmed;
        }
        if (descChanged && product.description !== descTrimmed) {
          productPatch.description = descTrimmed;
        }
        if (Object.keys(productPatch).length > 0) {
          updateProduct({ ...product, ...productPatch });
        }
      }
      onSuccess("Listing details updated on eBay");
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update listing");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !submitting && onClose()}
      ariaLabel="Edit details"
      panelClassName="w-[min(560px,92vw)] max-h-[85vh] flex flex-col"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground">Edit details</h2>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="text-muted-foreground hover:text-foreground disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-4 overflow-y-auto p-4">
        {loading && (
          <p className="text-center text-xs text-muted-foreground">
            Loading current eBay state…
          </p>
        )}
        {loadError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive bg-destructive/10 p-3 text-xs text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{loadError}</span>
          </div>
        )}
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between">
            <label className="text-xs font-medium text-muted-foreground">
              Title
            </label>
            <span className="text-[11px] text-muted-foreground">
              {titleTrimmed.length}/80
            </span>
          </div>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={titleLocked || submitting}
            maxLength={80}
            className={
              titleLocked
                ? "w-full rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground opacity-60"
                : "w-full rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground focus:border-secondary focus:outline-none disabled:opacity-50"
            }
          />
          {titleLocked && (
            <p className="flex items-start gap-1 text-[11px] text-muted-foreground">
              <Lock className="mt-0.5 h-3 w-3 shrink-0" />
              <span className="flex-1">{titleLockReason}</span>
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={submitting}
            rows={8}
            className="w-full rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground focus:border-secondary focus:outline-none disabled:opacity-50"
          />
        </div>
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive bg-destructive/10 p-3 text-xs text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="rounded-md bg-muted px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={
            submitting ||
            loading ||
            !!loadError ||
            (!titleChanged && !descChanged)
          }
          className="rounded-md bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? "Saving…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}
